/**
 * Locale context.
 *
 * The translator is built once per locale and memoised, because every component
 * that renders a string calls `t` and rebuilding it per render would allocate an
 * `Intl.PluralRules` on each pass.
 *
 * The stored locale is the *preference*, which may be `system`; the resolved
 * locale is always a shipped one. Nothing here is styling, so the move to
 * utility classes leaves it unchanged.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';

import {
  DEFAULT_LOCALE,
  LOCALES,
  createTranslator,
  detectLocale,
  isLocale,
  type Locale,
  type Translator,
} from '@yummy/shared';

import { usePersistentState } from '../lib/hooks';

/** `system` defers to the browser's language. */
export type LocalePreference = Locale | 'system';

const STORAGE_KEY = 'yummy.locale.v1';

interface LocaleValue {
  preference: LocalePreference;
  setPreference: (preference: LocalePreference) => void;
  /** The locale actually in force. */
  locale: Locale;
  t: Translator;
}

const defaultTranslator = createTranslator(DEFAULT_LOCALE);

const LocaleContext = createContext<LocaleValue>({
  preference: 'system',
  setPreference: () => undefined,
  locale: DEFAULT_LOCALE,
  t: defaultTranslator,
});

/**
 * Reads the active locale and translator.
 *
 * @returns The preference, the resolved locale, and `t`.
 */
export function useLocale(): LocaleValue {
  return useContext(LocaleContext);
}

/**
 * Shorthand for the translator.
 *
 * @returns The active `t` function.
 */
export function useT(): Translator {
  return useContext(LocaleContext).t;
}

/**
 * Holds the language preference for one app.
 *
 * @param props Children and an optional storage key.
 */
export function LocaleProvider({
  children,
  storageKey = STORAGE_KEY,
}: {
  children: ReactNode;
  storageKey?: string;
}) {
  const [stored, setPreference] = usePersistentState<LocalePreference>(storageKey, 'system');

  // An unrecognised stored value falls back to detection rather than to English,
  // so a browser set to Chinese is not silently overridden by a stale key.
  const preference: LocalePreference = stored === 'system' || isLocale(stored) ? stored : 'system';

  const locale: Locale = preference === 'system' ? detectLocale() : preference;

  const t = useMemo(() => createTranslator(locale), [locale]);

  const value = useMemo<LocaleValue>(
    () => ({ preference, setPreference, locale, t }),
    [preference, setPreference, locale, t]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export { LOCALES };
